import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from 'react-i18next';
import {useSnackbar} from "../../../contexts/SnackbarContext";

import {Box, CircularProgress, IconButton, Tooltip, Typography} from "@mui/material";
import {Header,} from "./DataSourceUpdateScheduler.styled";
import {ActionButton, OptionsButton} from "../../../components/data-sources/DataSourceTable.styled";
import {StyledTableContainer} from "../../../components/common/TableContainer.styled";
import {ReactComponent as Plus2Icon} from "../../../assets/icons/plus_2.svg";
import {ReactComponent as InfoSquareIcon} from "../../../assets/icons/info_square.svg";
import {ReactComponent as TrashIcon} from "../../../assets/icons/trash.svg";
import {ReactComponent as MoreIcon} from "../../../assets/icons/more.svg";
import {ReactComponent as EditIcon} from "../../../assets/icons/edit.svg";

import {jobsService} from "../../../services/jobs.service";
import {JobResponse, JobStatus, JobType} from "../../../models/jobs.models";
import {translateCronToHumanReadable} from "../../../utils/cron-utils";

import CreateUpdateSchedulerDialog from "../../../components/admin/CreateUpdateSchedulerDialog";
import ResponsiveTable, {ResponsiveTableColumn} from "../../../components/common/ResponsiveTable";
import ContextMenu from "../../../components/menu/ContextMenu";
import EditUpdateSchedulerDialog from "../../../components/admin/EditUpdateSchedulerDialog";

type DialogState =
    | { type: 'create' }
    | { type: 'edit'; job: JobResponse }
    | null;

const DataSourceUpdateScheduler = () => {
    const [jobs, setJobs] = useState<JobResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState<DialogState>(null);

    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const jobTypes = Object.values(JobType);

    const [contextMenu, setContextMenu] = useState<{
        mouseX?: number;
        mouseY?: number;
        anchorEl?: HTMLElement | null;
        job: JobResponse;
    } | null>(null);

    const handleContextMenu = useCallback((event: React.MouseEvent, job: JobResponse) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            anchorEl: event.currentTarget as HTMLElement,
            job,
        });
    }, []);

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        try {
            const jobs = await jobsService.getJobs();
            setJobs([...jobs].sort((a,b) => b.id - a.id));
        } catch (error) {
            console.error(t('dataSourceUpdateScheduler.messages.fetchJobsError'), error);
            showSnackbar(t('dataSourceUpdateScheduler.messages.fetchJobsError'), "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchJobs();
    }, [fetchJobs]);

    const handleDeleteJob = async (jobId: number) => {
        setLoading(true);

        try {
            await jobsService.deleteJob(jobId);

            showSnackbar(t('dataSourceUpdateScheduler.messages.deleteSuccess'), 'success');
            await fetchJobs();
        } catch (error: unknown) {
            showSnackbar(t('dataSourceUpdateScheduler.messages.deleteError'), "error");
            setLoading(false);
        }
    };

    const handleOpenEditDialog = (job: JobResponse) => {
        setOpenDialog({type: 'edit', job});
    };

    const formatDate = useCallback((dateString: string | number | null | undefined) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime()) || date.getFullYear() < 2000) return "-";
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    const columns: ResponsiveTableColumn<JobResponse>[] = useMemo(() => [
        {
            id: 'name',
            header: t('dataSourceUpdateScheduler.tableHeaders.dataSourceName'),
            render: (j) => j.name,
        },
        {
            id: 'type',
            header: t('dataSourceUpdateScheduler.tableHeaders.type'),
            render: (j) => j.type,
        },
        {
            id: 'lastUpdate',
            header: t('dataSourceUpdateScheduler.tableHeaders.lastUpdate'),
            render: (j) => formatDate(j.prevFireTime ?? null),
        },
        {
            id: 'nextUpdate',
            header: t('dataSourceUpdateScheduler.tableHeaders.nextUpdate'),
            render: (j) => formatDate(j.nextFireTime ?? null),
        },
        {
            id: 'status',
            header: t('dataSourceUpdateScheduler.tableHeaders.status'),
            render: (j) => (j.isRunning
                ? t(`dataSourceUpdateScheduler.status.${JobStatus.IN_PROGRESS}`)
                : t(`dataSourceUpdateScheduler.status.${JobStatus.SCHEDULED}`)),
        },
        {
            id: 'frequency',
            header: t('dataSourceUpdateScheduler.tableHeaders.frequency'),
            render: (j) => (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Typography variant="body2" component="span" sx={{fontFamily: 'monospace'}}>
                        {j.cronExpression}
                    </Typography>
                    <Tooltip title={translateCronToHumanReadable(j.cronExpression)}>
                        <IconButton size="small" sx={{color: 'primary.main'}} aria-label={translateCronToHumanReadable(j.cronExpression)}>
                            <InfoSquareIcon aria-hidden={true} focusable={false} style={{width: 18, height: 18}}/>
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
        {
            id: 'actions',
            header: t('dataSourceUpdateScheduler.tableHeaders.actions'),
            cellClassName: 'actions-cell',
            render: (j) => (
                <>
                    <ActionButton
                        className="mobile-only"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleContextMenu(e, j);
                        }}
                    >
                        {t('adminDataSourceList.actions')}
                    </ActionButton>
                    <OptionsButton
                        className="desktop-only"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleContextMenu(e, j);
                        }}
                    >
                        <MoreIcon aria-hidden={true} focusable={false}/>
                    </OptionsButton>
                </>
            ),
        }
    ], [t, formatDate, handleContextMenu]);


    return (loading
        ? (<CircularProgress />)
        : (
            <StyledTableContainer role="region" aria-labelledby="ds-update-scheduler-title">
                <Header>
                    <h2 id="ds-update-scheduler-title">{t('dataSourceUpdateScheduler.title')}</h2>

                    <ActionButton
                        variant="contained"
                        onClick={() => setOpenDialog({type: 'create' as const})}
                        startIcon={<Plus2Icon aria-hidden={true} focusable={false}/>}
                        aria-label={t('dataSourceUpdateScheduler.addButton') as string}
                        sx={{backgroundColor: 'primary.main'}}
                    >
                        {t('dataSourceUpdateScheduler.addButton')}
                    </ActionButton>
                </Header>

                <ResponsiveTable
                    columns={columns}
                    data={jobs}
                    getRowKey={(j) => j.id}
                    emptyState={t('dataSourceUpdateScheduler.noDataSources')}
                    ariaLabel={t('dataSourceUpdateScheduler.title') as string}
                />

                {openDialog?.type === 'create'  && (
                    <CreateUpdateSchedulerDialog
                        open
                        onClose={() => setOpenDialog(null)}
                        onJobCreated={fetchJobs}
                    />
                )}

                {openDialog?.type === 'edit' && (
                    <EditUpdateSchedulerDialog
                        open
                        currentFormData={openDialog.job}
                        onClose={() => setOpenDialog(null)}
                        onJobEdited={fetchJobs}
                    />
                )}

                {contextMenu && (
                    <ContextMenu
                        id={contextMenu ? `scheduler-menu-${contextMenu.job.id}` : undefined}
                        open={true}
                        anchorEl={contextMenu.anchorEl}
                        mouseX={contextMenu.mouseX}
                        mouseY={contextMenu.mouseY}
                        onClose={handleCloseContextMenu}
                        actions={[
                            {
                                key: 'edit',
                                label: t('common.edit'),
                                icon: <EditIcon style={{width: 16, height: 16}}/>,
                                onClick: () => handleOpenEditDialog(contextMenu.job),
                            },
                            {
                                key: 'delete',
                                label: t('common.delete'),
                                icon: <TrashIcon style={{width: 16, height: 16}}/>,
                                onClick: () => handleDeleteJob(contextMenu.job.id),
                                color: '#f44336',
                                hidden: !jobTypes.includes(contextMenu.job.type as JobType),
                            },
                        ]}
                    />)
                }
            </StyledTableContainer>
        )
    );
};

export default DataSourceUpdateScheduler;
